import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { apiClient } from '../../api/client';
import { toast } from 'sonner';
import { tenantTodayDate, toCalendarDateString, todayCalendarDate } from '../../lib/calendar-date';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { fmtCurrency } from '../../lib/display-labels';
import { RowError, rowErrorRingClass, groupRowErrors, type RowErrorReason } from '../ui/RowError';
import { unwrap } from '../../lib/api-envelope';
import ProvinceSelect, { regionName } from '../ProvinceSelect';
import { useAuth } from '../../contexts/AuthContext';
import {
  Plus,
  X,
  ChevronDown,
  ChevronRight,
  Paperclip,
  Upload,
  FileText,
  Trash2,
} from 'lucide-react';

/* ── types ── */

interface LineItem {
  key: number;
  description: string;
  qty: number;
  unit_price: number;
  /** A RATE SELECTION, not a tax name — see DEFAULT_LINE_TAX_CODE below. */
  taxCode: string;
  rate_card_item_id: string;
}

/** One choice in the per-line tax picker, as the API names it for this tenant. */
interface TaxCodeOption {
  code: string;
  label: string;
}

/**
 * What POST /invoices/price-preview answers. The screen renders these figures;
 * it computes none of them.
 */
interface PricePreview {
  jurisdiction: string | null;
  taxStatus: 'resolved' | 'exempt' | 'unconfigured';
  taxUnconfigured: boolean;
  taxUnconfiguredReason: string | null;
  taxExempt: boolean;
  pstExempt: boolean;
  pstExemptNumber: string | null;
  subtotal: number;
  taxLines: { label: string; rate: number; amount: number }[];
  /** null — never 0 — when the tenant has no rate on file. */
  taxTotal: number | null;
  total: number;
  availableTaxCodes: TaxCodeOption[];
  lines: { amount: number; taxCode: string }[];
}

interface RateCardItem {
  id: string;
  description: string;
  unit_price: number;
}

interface Customer {
  id: string;
  name: string;
  payment_terms?: string;
}

interface AttachedFile {
  file: File;
  id: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/* ── constants ── */

/**
 * THE PER-LINE CODE IS A RATE SELECTION, NOT A TAX NAME.
 *
 * This file used to hold two lookups that both had to go: a province list
 * labelled with rates ("BC (GST+PST 12%)") and a `TAX_RATES` map those labels
 * came from. Both were the frontend's own opinion about Canadian tax, and both
 * disagreed with the tenant's configured rates the moment a tenant configured
 * anything — a BC tenant on 5+6 saw 12% on screen and was charged 11%.
 *
 * The line picker used to offer none / GST / GST+PST / HST. The route branched
 * on 'none' and 'gst_pst' and nothing else, so 'hst' meant "rate 1 only" by
 * accident. That is not a missing case: WHICH taxes apply is a property of the
 * jurisdiction, and a line cannot know it. What a line CAN say is whether each
 * of the tenant's two configured rates reaches it.
 *
 * So the codes are `none` / `standard` / `rate_1_only`, the LABELS come from
 * `availableTaxCodes` on the price preview — the tenant's own words, for the
 * jurisdiction selected — and this file names no tax at all.
 */
const DEFAULT_LINE_TAX_CODE = 'standard';

/**
 * What the picker shows before the first preview lands, and if it never does.
 *
 * Deliberately unlabelled with any tax name: on a tenant this component has not
 * yet heard from, "GST" would be a guess, and a guess about tax printed beside
 * a price is the whole failure this change is about. It also keeps the choice
 * the operator already made on screen rather than collapsing the select onto a
 * value it has no option for.
 */
const FALLBACK_TAX_CODE_OPTIONS: TaxCodeOption[] = [
  { code: 'none', label: 'No tax' },
  { code: DEFAULT_LINE_TAX_CODE, label: 'Taxable' },
];

/**
 * Each source reads its own vertical's surface: Load and BOL read
 * /trucking/loads (trucking only, api rentals-haul item 1), Rental Order reads
 * /surface-rentals/orders (surface_rentals only). Offered to a tenant without
 * that vertical, picking one would end in a 403 rather than a list, so each is
 * offered only with its vertical. A casing tenant sees just None.
 */
const SOURCE_TYPES: { value: string; label: string; vertical?: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'load', label: 'Load', vertical: 'trucking' },
  { value: 'bol', label: 'BOL', vertical: 'trucking' },
  { value: 'rental_order', label: 'Rental Order', vertical: 'surface_rentals' },
];

/** The tenant's today, as the seed of a date input. '' only when the zone is
 *  unknown — an empty input the user must fill, never a guessed day. */
function todayISO(timeZone: string | null) {
  return todayCalendarDate(timeZone) ?? '';
}

/** The tenant's today plus the customer's payment terms, as an input seed. */
function dueDateFromTerms(timeZone: string | null, terms?: string): string {
  const days = terms ? parseInt(terms.replace(/\D/g, ''), 10) : NaN;
  const offset = isNaN(days) ? 30 : days;
  const d = tenantTodayDate(timeZone);
  if (!d) return '';
  d.setDate(d.getDate() + offset);
  return toCalendarDateString(d);
}

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

/** @see fmtCurrency — absence renders '—', not '$0.00'. */
const formatCurrency = (n: number | string | null | undefined) => fmtCurrency(n, 'CAD');

let nextKey = 1;

/* ── component ── */

/**
 * Manual invoice creation, and the switch that held it.
 *
 * ── WHY IT WAS HELD, AND WHAT LIFTS IT ──────────────────────────────────────
 * This modal became able to create an invoice at all in #536; every attempt
 * before that was refused at the route's arity check, which is why production
 * held ZERO invoices with source_type='manual' when it was turned off
 * (62 invoices, 0 manual, measured 2026-09-03). It was held because the tax on
 * that path was wrong three ways, none of which failed loudly:
 *
 *   1. THE KEY. Every row was sent as `tax_type`; POST /invoices reads
 *      `tax_code`. `tax_type` landed nowhere, so every line took the route's
 *      default and a row marked "None" was taxed anyway.
 *   2. THE REGION. The Tax Region select, and the `tax_region` /
 *      `tax_rate_*_label` / `tax_rate_*_pct` keys it sent, were read by
 *      NOTHING. The resolver takes `jurisdiction`, which this modal never sent.
 *   3. THE TOTAL. The on-screen figure came from a province/rate lookup in this
 *      file; the stored figure came from the tenant's configured rates.
 *
 * All three are closed by fieldiq-api #551 and by this file:
 *
 *   1. lines send `tax_code`, and the API refuses `tax_type` with a 400 that
 *      names the row rather than defaulting the line quietly;
 *   2. the region select sends `jurisdiction`, the key the resolver reads, and
 *      the four dead keys are gone;
 *   3. every figure below the line items comes from POST
 *      /invoices/price-preview — the SAME function the create path prices
 *      with. The screen cannot disagree with the stored row because it no
 *      longer computes anything to disagree with.
 *
 * ── THE SWITCH STAYS ────────────────────────────────────────────────────────
 * Kept rather than deleted, and kept as the ONE const both this file and the
 * Invoices header read. It is the thing that made the hold a two-line change
 * instead of a revert, and it will be again.
 */
export const MANUAL_INVOICE_CREATION_ENABLED: boolean = true;

export default function NewInvoiceModal({ open, onOpenChange }: Props) {
  /*
    The guard is a WRAPPER, not a branch inside the form, so the form component
    never mounts while the hold is on: no customer query fires, no preview
    fires, no line state exists, and `handleSubmit` is not reachable from any
    render. A `disabled` on the footer button would leave the whole form alive
    one prop away.
  */
  if (!MANUAL_INVOICE_CREATION_ENABLED) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md bg-fieldiq-card border-fieldiq-border">
          <DialogHeader>
            <DialogTitle>New Invoice</DialogTitle>
          </DialogHeader>
          <div role="alert" className="space-y-3 mt-2">
            <p className="text-sm text-white">
              Creating an invoice by hand is turned off while its tax
              calculation is corrected.
            </p>
            <p className="text-sm text-fieldiq-muted">
              The tax shown here would not have matched the tax stored on the
              invoice. Invoices generated from tickets are unaffected and price
              correctly.
            </p>
          </div>
          <div className="flex justify-end pt-2 border-t border-fieldiq-border">
            <Button
              variant="outline"
              className="border-fieldiq-border"
              onClick={() => onOpenChange(false)}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }
  return <ManualInvoiceForm open={open} onOpenChange={onOpenChange} />;
}

/**
 * The form itself. Exported so the row-error suite can keep pinning it while
 * the hold above is on — those assertions describe behaviour that must survive
 * the hold being lifted, and testing them through the default export would
 * mean deleting them today and rewriting them from memory later.
 */
export function ManualInvoiceForm({ open, onOpenChange }: Props) {
  const queryClient = useQueryClient();
  // Only for which region list to offer. Every tax figure comes from the API.
  const { company, hasVertical } = useAuth();
  const sourceTypes = SOURCE_TYPES.filter(s => !s.vertical || hasVertical(s.vertical));
  const timeZone = useTenantTimeZone();

  // ─── form state ───
  const [customerId, setCustomerId] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [issueDate, setIssueDate] = useState(todayISO(timeZone));
  const [dueDate, setDueDate] = useState(dueDateFromTerms(timeZone));
  /**
   * WHERE THE WORK WAS PERFORMED, which is what decides the tax — not the
   * customer's billing address, and not the tenant's home province.
   *
   * Blank means "the company's home province", which is what the resolver does
   * with an absent `jurisdiction`. The preview reports back which jurisdiction
   * actually priced, so a blank selection still names its answer on screen
   * rather than leaving the operator to assume.
   */
  const [jurisdiction, setJurisdiction] = useState('');
  const [notes, setNotes] = useState('');

  // source link
  const [showSourceLink, setShowSourceLink] = useState(false);
  const [sourceType, setSourceType] = useState('none');
  const [sourceId, setSourceId] = useState('');

  // line items
  const [lines, setLines] = useState<LineItem[]>([
    { key: nextKey++, description: '', qty: 1, unit_price: 0, taxCode: DEFAULT_LINE_TAX_CODE, rate_card_item_id: '' },
  ]);

  // rate card picker per row
  const [rateCardOpenRow, setRateCardOpenRow] = useState<number | null>(null);

  // attachments
  const [showAttachments, setShowAttachments] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /**
   * The row keys at each position of the array the LAST submit actually sent.
   *
   * A ref, not state: it is read inside the mutation's error handler, which
   * closes over the render that fired it, and it must describe the request that
   * failed rather than whatever the form looks like by the time the answer
   * lands. The operator can edit a row while the request is in flight.
   */
  const sentLineKeysRef = useRef<number[]>([]);

  // validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);

  /**
   * The server's per-line refusals, keyed by the row's STABLE `key` rather than
   * by its position.
   *
   * `details[].index` is a position in the array this modal SENT, and the sent
   * array is not `lines` — `buildLines` drops every row with a blank
   * description. So index 1 of the refusal is not `lines[1]`, and keying this
   * map by position would put a red ring on an innocent row while the row that
   * actually failed looked fine. `buildLines` returns the key at each sent
   * position so the two can be reconciled once, at the point of failure.
   *
   * Keys are stable across add and remove, so unlike the ticket editors this
   * needs no re-indexing when a row is deleted — see lib/line-item-rows.ts for
   * the version that does.
   */
  const [lineErrors, setLineErrors] = useState<Record<number, RowErrorReason[]>>({});
  /**
   * Refusals that could not be attributed to a row on screen.
   *
   * The invoice route answers with a SEPARATE details[] for `attachments`, whose
   * `index` counts a different array — index 2 means one thing for lines and
   * another for attachments, and nothing in the entry itself says which. This
   * modal does not send `attachments` today, so that array cannot arrive; the
   * guard is here because the cost of guessing wrong is a mark on a row the
   * operator never touched, which is the failure this whole component exists to
   * end. Anything whose index names no sent row is shown as words instead.
   */
  const [unattributed, setUnattributed] = useState<RowErrorReason[]>([]);

  // ─── queries ───
  const { data: customersRaw } = useQuery({
    queryKey: ['invoice-customers'],
    queryFn: () => apiClient.get('/customers').then(r => r.data?.data ?? r.data ?? []),
    staleTime: 60_000,
    enabled: open,
  });

  const customers: Customer[] = useMemo(() => {
    const list = Array.isArray(customersRaw) ? customersRaw : [];
    return list.map((c: any) => ({
      id: c.id,
      name: c.name || c.company_name || c.companyName || '',
      payment_terms: c.payment_terms || c.paymentTerms || '',
    }));
  }, [customersRaw]);

  const filteredCustomers = useMemo(() => {
    if (!customerSearch) return customers;
    const q = customerSearch.toLowerCase();
    return customers.filter(c => c.name.toLowerCase().includes(q));
  }, [customers, customerSearch]);

  const selectedCustomer = customers.find(c => c.id === customerId);

  // Rate card items for selected customer
  const { data: rateCardItemsRaw } = useQuery({
    queryKey: ['rate-card-items', customerId],
    queryFn: () => apiClient.get('/rate-cards/items', { params: { customer_id: customerId } }).then(r => r.data?.data ?? r.data ?? []),
    staleTime: 60_000,
    enabled: open && !!customerId,
  });

  const rateCardItems: RateCardItem[] = useMemo(() => {
    const list = Array.isArray(rateCardItemsRaw) ? rateCardItemsRaw : [];
    return list.map((item: any) => ({
      id: item.id,
      description: item.description || item.name || '',
      unit_price: parseFloat(item.unit_price || item.unitPrice || '0'),
    }));
  }, [rateCardItemsRaw]);

  // Source documents for selected customer + source type
  const { data: sourceDocsRaw } = useQuery({
    queryKey: ['source-docs', sourceType, customerId],
    queryFn: () => {
      if (sourceType === 'load') return apiClient.get('/trucking/loads', { params: { customer_id: customerId, status: 'active' } }).then(r => r.data?.data ?? r.data ?? []);
      if (sourceType === 'bol') return apiClient.get('/trucking/loads/bols', { params: { customer_id: customerId } }).then(r => r.data?.data ?? r.data ?? []);
      if (sourceType === 'rental_order') return apiClient.get('/surface-rentals/orders', { params: { customer_id: customerId, status: 'active' } }).then(r => r.data?.data ?? r.data ?? []);
      return Promise.resolve([]);
    },
    staleTime: 30_000,
    enabled: open && !!customerId && sourceType !== 'none',
  });

  const sourceDocs: { id: string; label: string }[] = useMemo(() => {
    const list = Array.isArray(sourceDocsRaw) ? sourceDocsRaw : [];
    return list.map((d: any) => ({
      id: d.id,
      label: d.bol_number || d.load_number || d.order_number || d.name || d.id?.slice(0, 8) || '',
    }));
  }, [sourceDocsRaw]);

  // ─── line item helpers ───
  const updateLine = useCallback((key: number, field: keyof LineItem, value: string | number) => {
    setLines(prev => prev.map(l => l.key === key ? { ...l, [field]: value } : l));
    // Touching the row retires the server's verdict on it. The verdict describes
    // what was sent, and this row is no longer what was sent.
    setLineErrors(prev => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const removeLine = useCallback((key: number) => {
    setLines(prev => prev.filter(l => l.key !== key));
    setLineErrors(prev => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const addLine = useCallback(() => {
    setLines(prev => [...prev, { key: nextKey++, description: '', qty: 1, unit_price: 0, taxCode: DEFAULT_LINE_TAX_CODE, rate_card_item_id: '' }]);
  }, []);

  /**
   * The `lines` array as the API will receive it, plus the row key sitting at
   * each sent position.
   *
   * One function so the payload and the index map can never disagree. They were
   * two expressions in two places for as long as it took to write this comment,
   * and that is exactly long enough for a later edit to add a filter to one of
   * them: every refusal after the dropped row would then mark the row below the
   * one that failed, silently and only on the error path.
   */
  const buildLines = useCallback(() => {
    const kept = lines.filter(l => l.description.trim());
    return {
      keys: kept.map(l => l.key),
      payload: kept.map(l => ({
        description: l.description,
        quantity: l.qty,
        unit_price: l.unit_price,
        // `tax_code`, not `tax_type`, and 'none' is SENT rather than nulled.
        // A null here means "this line states nothing", which the API prices at
        // every configured rate — the exact opposite of what "None" means on
        // screen. The old code sent `tax_type: null` for a "None" row and the
        // row was fully taxed.
        tax_code: l.taxCode,
        rate_card_item_id: l.rate_card_item_id || null,
      })),
    };
  }, [lines]);

  const pickRateCard = useCallback((lineKey: number, item: RateCardItem) => {
    setLines(prev => prev.map(l =>
      l.key === lineKey
        ? { ...l, description: item.description, unit_price: item.unit_price, rate_card_item_id: item.id }
        : l
    ));
    setRateCardOpenRow(null);
    setLineErrors(prev => {
      if (!prev[lineKey]) return prev;
      const next = { ...prev };
      delete next[lineKey];
      return next;
    });
  }, []);

  // ─── the price, from the API that will store it ───
  //
  // NOT COMPUTED HERE. This modal used to derive its own tax from a province
  // lookup in this file while the API priced from the tenant's configured
  // rates — two answers to one question, and the operator only ever saw one of
  // them. Everything below the line items is now the preview endpoint's answer,
  // produced by the same function POST /invoices prices with.
  //
  // A per-row `amount` is still shown from `qty × unit_price`. That is
  // arithmetic the operator typed, not a figure the API decides, and it has to
  // update on every keystroke; the preview's own per-line amounts are used to
  // cross-check it once they land (see `previewLineAmount`).

  /** Exactly the body a Save would POST, minus the fields that cannot price. */
  const previewBody = useMemo(() => ({
    customer_id: customerId,
    issue_date: issueDate || null,
    jurisdiction: jurisdiction || null,
    lines: buildLines().payload,
  }), [customerId, issueDate, jurisdiction, buildLines]);

  /**
   * The body, settled.
   *
   * Debounced because it changes on every keystroke in a price field and each
   * change is a round trip. 350ms is long enough that typing "1250" is one
   * request rather than four, and short enough that the total is current before
   * the operator reaches the Save button.
   */
  const [settledBody, setSettledBody] = useState(previewBody);
  useEffect(() => {
    const t = setTimeout(() => setSettledBody(previewBody), 350);
    return () => clearTimeout(t);
  }, [previewBody]);

  const {
    data: preview,
    isFetching: previewFetching,
    isError: previewFailed,
  } = useQuery<PricePreview | null>({
    queryKey: ['invoice-price-preview', settledBody],
    queryFn: () => apiClient.post('/invoices/price-preview', settledBody)
      .then(r => unwrap<PricePreview>(r.data)),
    // An explicit unwrap, not `res.data?.data || res.data`: when the server
    // answers `{success: true, data: null}` the `||` falls through to the
    // ENVELOPE, which is truthy, and every field then reads undefined.
    enabled: open && !!customerId,
    staleTime: 30_000,
    retry: false,
  });

  /**
   * The company's HOME jurisdiction — the one the resolver falls back to when
   * nothing is selected — latched from the only preview that can report it.
   *
   * The "company default" option NAMES its fallback rather than saying "None",
   * because a blank selection does not mean no tax. It used to name it from
   * `preview.jurisdiction`, which is the jurisdiction that actually PRICED —
   * and that is the operator's own selection the moment they make one. So the
   * fallback option renamed itself after whatever was picked: select British
   * Columbia on an Alberta tenant and the option read "British Columbia
   * (company default)", and choosing it priced Alberta. The label named the
   * wrong province at the one moment it was being used to decide, which is the
   * failure this file's own comment two lines down was written to prevent.
   *
   * A preview requested with `jurisdiction: null` is the only one whose answer
   * IS the home province — every other one echoes the selection back. So that
   * is the only one worth latching, and it stays latched because a tenant's
   * home province does not change while the modal is open.
   */
  const [homeJurisdiction, setHomeJurisdiction] = useState<string | null>(null);
  useEffect(() => {
    if (settledBody.jurisdiction === null && preview?.jurisdiction) {
      setHomeJurisdiction(preview.jurisdiction);
    }
  }, [settledBody, preview]);

  /**
   * The line picker's choices, in the tenant's own words for the selected
   * jurisdiction.
   *
   * The fallback names no tax (see FALLBACK_TAX_CODE_OPTIONS). A choice the
   * operator has already made is appended if the current options do not carry
   * it, because a select cannot show a value it has no option for — it shows
   * the FIRST one while state quietly keeps the real value, and the screen then
   * disagrees with what will be sent.
   */
  const taxCodeOptions = useMemo(() => {
    const base = preview?.availableTaxCodes?.length
      ? preview.availableTaxCodes
      : FALLBACK_TAX_CODE_OPTIONS;
    const chosen = lines.map(l => l.taxCode);
    const missing = chosen.filter((c, i) => chosen.indexOf(c) === i && !base.some(o => o.code === c));
    return missing.length ? [...base, ...missing.map(code => ({ code, label: code }))] : base;
  }, [preview, lines]);

  /** The preview's own amount for a sent row, or null while none has landed. */
  const previewLineAmount = useCallback((key: number): number | null => {
    if (!preview?.lines) return null;
    const sentIndex = buildLines().keys.indexOf(key);
    if (sentIndex < 0) return null;
    return preview.lines[sentIndex]?.amount ?? null;
  }, [preview, buildLines]);

  // ─── customer change handler ───
  const handleCustomerChange = useCallback((id: string) => {
    setCustomerId(id);
    const cust = customers.find(c => c.id === id);
    if (cust) {
      setDueDate(dueDateFromTerms(timeZone, cust.payment_terms));
    }
    // Reset source when customer changes
    setSourceType('none');
    setSourceId('');
  }, [customers, timeZone]);

  // ─── file handling ───
  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(e.target.files || []);
    setAttachedFiles(prev => [
      ...prev,
      ...newFiles.map(f => ({ file: f, id: `${Date.now()}-${Math.random().toString(36).slice(2)}` })),
    ]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const removeFile = useCallback((id: string) => {
    setAttachedFiles(prev => prev.filter(f => f.id !== id));
  }, []);

  // ─── validation ───
  const validate = useCallback((): Record<string, string> => {
    const errs: Record<string, string> = {};
    if (!customerId) errs.customer = 'Customer is required';
    if (!issueDate) errs.issueDate = 'Issue date is required';
    if (lines.length === 0) {
      errs.lines = 'At least one line item is required';
    } else {
      const hasValid = lines.some(l => l.description.trim() && l.qty > 0 && l.unit_price >= 0);
      if (!hasValid) errs.lines = 'At least one line item must have a description and quantity > 0';
    }
    return errs;
  }, [customerId, issueDate, lines]);

  const isValid = useMemo(() => Object.keys(validate()).length === 0, [validate]);

  // ─── mutation ───
  const createInvoice = useMutation({
    mutationFn: async (payload: any) => {
      const res = await apiClient.post('/invoices', payload);
      return res.data;
    },
    onSuccess: () => {
      toast.success('Invoice draft created');
      queryClient.invalidateQueries({ queryKey: ['unified-invoices'] });
      resetAndClose();
    },
    /**
     * A refusal about N lines says WHICH line.
     *
     * Gated on `details` being an array rather than on the status code. The
     * status is not the discriminator here: POST /invoices answers 400 for a bad
     * line, and the sibling payroll route answers 404 when a row points at
     * another company's worker while still naming the rows. Keying on 400 would
     * read the 404's details as absent and drop them — the row goes unmarked and
     * the banner is all that is left, which is the state this replaced.
     */
    onError: (err: any) => {
      const data = err?.response?.data;
      const msg = data?.error || 'Failed to create invoice';

      if (Array.isArray(data?.details)) {
        const keys = sentLineKeysRef.current;
        const byKey: Record<number, RowErrorReason[]> = {};
        const strays: RowErrorReason[] = [];
        for (const [index, reasons] of Object.entries(groupRowErrors(data.details))) {
          const key = keys[Number(index)];
          if (key === undefined) strays.push(...reasons);
          else byKey[key] = reasons;
        }
        setLineErrors(byKey);
        setUnattributed(strays);
      } else {
        // A run-level refusal — "At least one line is required", a permission
        // wall — is about the request, not a row. Clearing rather than leaving
        // the previous attempt's marks up: a red ring that survives the edit
        // that fixed it is a lie about the current state of the form.
        setLineErrors({});
        setUnattributed([]);
      }

      toast.error(msg);
    },
  });

  // ─── submit ───
  const handleSubmit = () => {
    setSubmitted(true);
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    // Last attempt's marks come down as the next one goes out, or a row the
    // server has not judged yet still wears the previous verdict.
    setLineErrors({});
    setUnattributed([]);

    const { keys, payload: linePayload } = buildLines();
    sentLineKeysRef.current = keys;

    const payload: any = {
      customer_id: customerId,
      issue_date: issueDate,
      due_date: dueDate || null,
      // `jurisdiction` — WHERE THE WORK WAS PERFORMED — is the key the tax
      // resolver reads. This modal used to send `tax_region` plus four
      // `tax_rate_*` keys carrying its own idea of the rates; `tax_region` has
      // no reader anywhere in the API and the rate keys were ignored, so the
      // operator's province selection reached nothing and every manual invoice
      // priced at the tenant's HOME province whatever the screen showed.
      //
      // Blank stays absent rather than becoming a province: the resolver's own
      // answer for "no jurisdiction stated" is the home one, and encoding that
      // here would be a second place that decides it.
      jurisdiction: jurisdiction || null,
      notes: notes || null,
      source: 'manual',
      status: 'draft',
      // `lines`, not `line_items`. POST /invoices reads `body.lines` and has
      // since it was written; this modal has sent `line_items` since PR #4, so
      // every Create Invoice from this screen was refused at the first check
      // with "At least one line is required" no matter what the operator typed.
      // Measured against a running API 2026-09-03, both spellings, same body.
      //
      // The row-level details[] this component now renders is unreachable until
      // this line is right: the request never gets past the arity check to the
      // per-line validation that produces them.
      lines: linePayload,
    };

    // source link
    if (sourceType === 'load' && sourceId) payload.source_load_id = sourceId;
    if (sourceType === 'bol' && sourceId) payload.source_bol_id = sourceId;
    if (sourceType === 'rental_order' && sourceId) payload.source_rental_order_id = sourceId;

    // Attachments are uploaded after creation via the uploads API;
    // store the file list for a follow-up step if needed.
    // For now, we include file metadata so the API knows to expect them.
    if (attachedFiles.length > 0) {
      payload.attachment_count = attachedFiles.length;
    }

    createInvoice.mutate(payload);
  };

  // ─── reset ───
  const resetAndClose = () => {
    setCustomerId('');
    setCustomerSearch('');
    setIssueDate(todayISO(timeZone));
    setDueDate(dueDateFromTerms(timeZone));
    setJurisdiction('');
    setNotes('');
    setShowSourceLink(false);
    setSourceType('none');
    setSourceId('');
    setLines([{ key: nextKey++, description: '', qty: 1, unit_price: 0, taxCode: DEFAULT_LINE_TAX_CODE, rate_card_item_id: '' }]);
    setAttachedFiles([]);
    setErrors({});
    // Server verdicts are per-row and the rows are gone. Reopening the modal on
    // a fresh blank line still wearing the last invoice's red ring would be the
    // stale-marker bug with a longer fuse.
    setLineErrors({});
    setUnattributed([]);
    sentLineKeysRef.current = [];
    setSubmitted(false);
    setRateCardOpenRow(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl bg-fieldiq-card border-fieldiq-border">
        <DialogHeader>
          <DialogTitle>New Invoice</DialogTitle>
        </DialogHeader>

        <div className="space-y-5 mt-2">
          {/* ─── Top fields ─── */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {/* Customer */}
            <div className="col-span-2">
              <Label className="text-xs text-fieldiq-muted">Customer *</Label>
              <div className="relative mt-1">
                <input
                  type="text"
                  value={customerId ? (selectedCustomer?.name || '') : customerSearch}
                  onChange={e => {
                    setCustomerSearch(e.target.value);
                    if (customerId) setCustomerId('');
                  }}
                  onFocus={() => { if (customerId) { setCustomerSearch(selectedCustomer?.name || ''); setCustomerId(''); } }}
                  placeholder="Search customers..."
                  className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-fieldiq-accent/50 transition-colors"
                />
                {!customerId && customerSearch && filteredCustomers.length > 0 && (
                  <div className="absolute z-50 mt-1 w-full bg-fieldiq-card border border-fieldiq-border rounded-lg shadow-xl max-h-48 overflow-y-auto">
                    {filteredCustomers.map(c => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => { handleCustomerChange(c.id); setCustomerSearch(''); }}
                        className="w-full text-left px-3 py-2 text-sm text-white hover:bg-fieldiq-border/50 transition-colors"
                      >
                        {c.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {submitted && errors.customer && (
                <p className="text-xs text-red-400 mt-1">{errors.customer}</p>
              )}
            </div>

            {/* Issue Date */}
            <div>
              <Label className="text-xs text-fieldiq-muted">Issue Date *</Label>
              <Input
                type="date"
                className="mt-1 bg-fieldiq-dark border-fieldiq-border"
                value={issueDate}
                onChange={e => setIssueDate(e.target.value)}
              />
              {submitted && errors.issueDate && (
                <p className="text-xs text-red-400 mt-1">{errors.issueDate}</p>
              )}
            </div>

            {/* Due Date */}
            <div>
              <Label className="text-xs text-fieldiq-muted">Due Date</Label>
              <Input
                type="date"
                className="mt-1 bg-fieldiq-dark border-fieldiq-border"
                value={dueDate}
                onChange={e => setDueDate(e.target.value)}
              />
            </div>

            {/* Where the work was performed — this is what decides the tax. */}
            <div className="col-span-2">
              <Label className="text-xs text-fieldiq-muted" htmlFor="invoice-jurisdiction">
                Work performed in
              </Label>
              <ProvinceSelect
                id="invoice-jurisdiction"
                aria-label="Work performed in"
                value={jurisdiction}
                onChange={setJurisdiction}
                country={company?.country === 'US' ? 'US' : 'CA'}
                placeholder={
                  homeJurisdiction
                    ? `${regionName(homeJurisdiction)} (company default)`
                    : 'Company default'
                }
                className="mt-1 w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-fieldiq-accent/50"
              />
              {/*
                The placeholder NAMES the fallback rather than saying "None".
                The resolver prices a blank selection at the company's home
                province, so "None" was a lie about what would happen — and the
                old options carried rates of their own ("BC (GST+PST 12%)")
                that were this file's opinion, not the tenant's configuration.
              */}
              <p className="text-[11px] text-fieldiq-muted mt-1">
                Where the work was done, not the customer's billing address.
              </p>
            </div>
          </div>

          {/* ─── Source Link (collapsible) ─── */}
          <div className="border-t border-fieldiq-border pt-3">
            <button
              type="button"
              className="flex items-center gap-1.5 text-xs font-medium text-fieldiq-muted hover:text-white transition-colors"
              onClick={() => setShowSourceLink(!showSourceLink)}
            >
              {showSourceLink ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              Link to source document
            </button>
            {showSourceLink && (
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-fieldiq-muted">Source Type</Label>
                  <select
                    value={sourceType}
                    onChange={e => { setSourceType(e.target.value); setSourceId(''); }}
                    className="mt-1 w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-fieldiq-accent/50"
                  >
                    {sourceTypes.map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
                {sourceType !== 'none' && (
                  <div>
                    <Label className="text-xs text-fieldiq-muted">Select {SOURCE_TYPES.find(s => s.value === sourceType)?.label}</Label>
                    <select
                      value={sourceId}
                      onChange={e => setSourceId(e.target.value)}
                      className="mt-1 w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-fieldiq-accent/50"
                    >
                      <option value="">Select...</option>
                      {sourceDocs.map(d => (
                        <option key={d.id} value={d.id}>{d.label}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ─── Line Items ─── */}
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-fieldiq-muted mb-2">Line Items</p>
            {submitted && errors.lines && (
              <p className="text-xs text-red-400 mb-2">{errors.lines}</p>
            )}
            {unattributed.length > 0 && (
              <RowError
                reasons={unattributed}
                label="Not saved: "
                className="mb-2"
              />
            )}

            {/* Header */}
            <div className="hidden md:grid grid-cols-[1fr_70px_100px_100px_90px_36px] gap-2 px-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-fieldiq-muted">
              <span>Description</span>
              <span className="text-right">Qty</span>
              <span className="text-right">Unit Price</span>
              <span>Tax</span>
              <span className="text-right">Amount</span>
              <span />
            </div>

            {/* Rows */}
            <div className="space-y-2">
              {lines.map(line => {
                // The API's amount for this row once a preview covering it
                // has landed; the operator's own arithmetic until then, so the
                // cell tracks typing instead of lagging a round trip behind.
                // Rounding lives on one side only — theirs.
                const amount = previewLineAmount(line.key) ?? (line.qty * line.unit_price);
                const rowReasons = lineErrors[line.key];
                return (
                  <div key={line.key} data-testid="invoice-line-row">
                  <div className={`grid grid-cols-1 md:grid-cols-[1fr_70px_100px_100px_90px_36px] gap-2 items-start bg-fieldiq-dark/30 rounded-lg px-2 py-2 ${rowErrorRingClass(!!rowReasons)}`}>
                    {/* Description + rate card picker */}
                    <div className="relative">
                      <input
                        type="text"
                        value={line.description}
                        onChange={e => updateLine(line.key, 'description', e.target.value)}
                        placeholder="Description"
                        className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-fieldiq-accent/50"
                      />
                      {rateCardItems.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setRateCardOpenRow(rateCardOpenRow === line.key ? null : line.key)}
                          className="absolute right-2 top-2 text-[10px] text-fieldiq-accent hover:text-fieldiq-accent/80 font-medium"
                        >
                          Rate card
                        </button>
                      )}
                      {rateCardOpenRow === line.key && (
                        <div className="absolute z-50 mt-1 w-full bg-fieldiq-card border border-fieldiq-border rounded-lg shadow-xl max-h-40 overflow-y-auto">
                          {rateCardItems.map(item => (
                            <button
                              key={item.id}
                              type="button"
                              onClick={() => pickRateCard(line.key, item)}
                              className="w-full text-left px-3 py-2 text-sm text-white hover:bg-fieldiq-border/50 transition-colors flex justify-between"
                            >
                              <span>{item.description}</span>
                              <span className="text-fieldiq-muted">{formatCurrency(item.unit_price)}</span>
                            </button>
                          ))}
                          {rateCardItems.length === 0 && (
                            <p className="px-3 py-2 text-xs text-fieldiq-muted">No rate card items</p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Qty */}
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={line.qty}
                      onChange={e => updateLine(line.key, 'qty', parseFloat(e.target.value) || 0)}
                      className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-2 py-2 text-white text-sm text-right focus:outline-none focus:border-fieldiq-accent/50"
                    />

                    {/* Unit Price */}
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={line.unit_price}
                      onChange={e => updateLine(line.key, 'unit_price', parseFloat(e.target.value) || 0)}
                      className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-2 py-2 text-white text-sm text-right focus:outline-none focus:border-fieldiq-accent/50"
                    />

                    {/* Tax — labelled by the tenant's own configuration */}
                    <select
                      value={line.taxCode}
                      onChange={e => updateLine(line.key, 'taxCode', e.target.value)}
                      aria-label="Tax treatment"
                      className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-2 py-2 text-sm text-white focus:outline-none focus:border-fieldiq-accent/50"
                    >
                      {taxCodeOptions.map(o => (
                        <option key={o.code} value={o.code}>{o.label}</option>
                      ))}
                    </select>

                    {/* Amount (computed) */}
                    <span className="text-sm font-semibold text-white text-right py-2 pr-1">
                      {formatCurrency(amount)}
                    </span>

                    {/* Remove */}
                    <button
                      type="button"
                      onClick={() => removeLine(line.key)}
                      disabled={lines.length <= 1}
                      className="p-2 text-fieldiq-muted hover:text-red-400 disabled:opacity-30 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  {/*
                    Under the row it belongs to, never only in the toast. The
                    toast carries the API's one-sentence summary, which names
                    the FIRST offending line and no others — a three-bad-row
                    invoice was one sentence and twelve identical-looking rows.
                  */}
                  {rowReasons && <RowError reasons={rowReasons} className="px-2 pb-1" />}
                  </div>
                );
              })}
            </div>

            {/* Add line */}
            <button
              type="button"
              onClick={addLine}
              className="flex items-center gap-1.5 mt-2 text-xs font-medium text-fieldiq-accent hover:text-fieldiq-accent/80 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" /> Add Line
            </button>

            {/*
              Totals — EVERY figure here is the API's, from the same function
              that will price the stored invoice. Nothing here is
              computed locally, which is what makes the screen and the row one
              number instead of two.
            */}
            <div
              data-testid="invoice-totals"
              aria-busy={previewFetching || undefined}
              className="mt-3 border-t border-fieldiq-border pt-3 space-y-1.5"
            >
              <div className="flex justify-between text-sm">
                <span className="text-fieldiq-muted">Subtotal</span>
                <span className="text-white font-semibold">{formatCurrency(preview?.subtotal)}</span>
              </div>

              {preview?.taxLines.map(t => (
                <div key={t.label} className="flex justify-between text-sm">
                  <span className="text-fieldiq-muted">{t.label} ({t.rate}%)</span>
                  <span className="text-white">{formatCurrency(t.amount)}</span>
                </div>
              ))}

              {/*
                A tax the tenant has not configured is UNKNOWN, not zero. The
                API answers `taxTotal: null` and flags it; rendering an em dash
                and the reason is the only honest thing to show. A "$0.00" here
                is a number the operator would have signed off.
              */}
              {preview?.taxUnconfigured && (
                <div role="alert" className="flex justify-between text-sm">
                  <span className="text-amber-400">
                    {preview.taxUnconfiguredReason || 'Tax rate not configured'}
                  </span>
                  <span className="text-amber-400">{formatCurrency(preview.taxTotal)}</span>
                </div>
              )}

              {preview?.taxExempt && (
                <p className="text-xs text-fieldiq-muted">This customer is tax exempt.</p>
              )}
              {preview?.pstExempt && (
                <p className="text-xs text-fieldiq-muted">
                  PST not charged — customer is PST exempt
                  {preview.pstExemptNumber ? ` (${preview.pstExemptNumber})` : ''}. GST still applies.
                </p>
              )}

              <div className="flex justify-between text-sm font-bold">
                <span className="text-white">Total</span>
                <span className="text-fieldiq-accent">{formatCurrency(preview?.total)}</span>
              </div>

              {/*
                A failed preview shows NO total rather than a stale or a
                locally-guessed one. `fmtCurrency` already renders an absent
                value as an em dash, so the figures above go blank on their own;
                this says why, because a row of dashes with no explanation reads
                as a bug in the form rather than as a request that did not land.
              */}
              {previewFailed && (
                <p role="alert" className="text-xs text-red-400">
                  Could not price this invoice. The total will be calculated when it saves.
                </p>
              )}
              {preview?.jurisdiction && (
                <p className="text-[11px] text-fieldiq-muted">
                  Priced for {regionName(preview.jurisdiction)}.
                </p>
              )}
            </div>
          </div>

          {/* ─── Attachments (collapsible) ─── */}
          <div className="border-t border-fieldiq-border pt-3">
            <button
              type="button"
              className="flex items-center gap-1.5 text-xs font-medium text-fieldiq-muted hover:text-white transition-colors"
              onClick={() => setShowAttachments(!showAttachments)}
            >
              {showAttachments ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              <Paperclip className="w-3.5 h-3.5" />
              Attachments {attachedFiles.length > 0 && `(${attachedFiles.length})`}
            </button>
            {showAttachments && (
              <div className="mt-3 space-y-2">
                {/* Dropzone */}
                <label className="flex items-center justify-center gap-2 border-2 border-dashed border-fieldiq-border rounded-lg px-4 py-4 cursor-pointer hover:border-fieldiq-accent/40 transition-colors">
                  <Upload className="w-5 h-5 text-fieldiq-muted" />
                  <span className="text-sm text-fieldiq-muted">Click to attach files</span>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={handleFileSelect}
                  />
                </label>

                {/* File list */}
                {attachedFiles.length > 0 && (
                  <div className="space-y-1">
                    {attachedFiles.map(af => (
                      <div key={af.id} className="flex items-center gap-2 bg-fieldiq-dark/30 rounded-lg px-3 py-2">
                        <FileText className="w-4 h-4 text-fieldiq-muted shrink-0" />
                        <span className="text-sm text-white truncate flex-1">{af.file.name}</span>
                        <span className="text-xs text-fieldiq-muted">{formatBytes(af.file.size)}</span>
                        <button
                          type="button"
                          onClick={() => removeFile(af.id)}
                          className="p-1 text-fieldiq-muted hover:text-red-400 transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ─── Notes ─── */}
          <div>
            <Label className="text-xs text-fieldiq-muted">Notes</Label>
            <Textarea
              className="mt-1 bg-fieldiq-dark border-fieldiq-border resize-none"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
              placeholder="Internal notes (optional)..."
            />
          </div>

          {/* ─── Footer ─── */}
          <div className="flex gap-3 pt-2 border-t border-fieldiq-border">
            <Button
              className="flex-1 bg-fieldiq-accent text-white hover:bg-fieldiq-accent/90 font-semibold"
              onClick={handleSubmit}
              disabled={createInvoice.isPending || (submitted && !isValid)}
            >
              {createInvoice.isPending ? 'Saving...' : 'Save as Draft'}
            </Button>
            <Button
              variant="outline"
              className="border-fieldiq-border"
              onClick={resetAndClose}
              disabled={createInvoice.isPending}
            >
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
